import React from 'react';
import { Column, Table } from '@tanstack/react-table';
import { Hash, ListFilter, Plus, Type, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { cn } from '../../../lib/utils';
import type { CatalogueRow as StyleRow } from '../../../lib/catalogueAdminApi';
import { describeFilter, FilterEditor } from './FilterEditors';

interface Props {
  table: Table<StyleRow>;
  editing: string | null;
  onEditingChange: (id: string | null) => void;
  onReset: () => void;
}

const KindIcon = ({ kind }: { kind?: string }) => {
  if (kind === 'range') return <Hash className="h-3.5 w-3.5 text-[color:var(--oa-muted)]" />;
  if (kind === 'text') return <Type className="h-3.5 w-3.5 text-[color:var(--oa-muted)]" />;
  return <ListFilter className="h-3.5 w-3.5 text-[color:var(--oa-muted)]" />;
};

const Chip: React.FC<{ column: Column<StyleRow, unknown>; open: boolean; onOpenChange: (o: boolean) => void }> = ({ column, open, onOpenChange }) => {
  const label = column.columnDef.meta?.label ?? column.id;
  const summary = describeFilter(column);
  const active = summary !== null;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'group inline-flex h-8 max-w-[320px] items-center gap-1.5 rounded-md border pl-2.5 pr-1 text-[12.5px] transition-colors',
            active ? 'border-[color:color-mix(in_oklab,var(--oa-accent)_35%,transparent)] bg-[color:var(--oa-accent-soft)] text-[color:var(--oa-text)]' : 'border-dashed border-[color:var(--oa-line-3)] bg-[color:var(--oa-card)] text-[color:var(--oa-muted)]'
          )}
        >
          <span className="text-[color:var(--oa-muted)]">{label}</span>
          {active ? (
            <>
              <span className="text-[color:var(--oa-muted)]">is</span>
              <span className="truncate font-medium">{summary}</span>
            </>
          ) : (
            <span className="text-[color:var(--oa-muted)]">…</span>
          )}
          <span
            role="button"
            aria-label={`Remove ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              column.setFilterValue(undefined);
              onOpenChange(false);
            }}
            className="ml-0.5 rounded p-0.5 text-[color:var(--oa-muted)] hover:bg-black/5 hover:text-[color:var(--oa-text)]"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-[280px] max-w-[420px] rounded-xl border-[color:var(--oa-line-2)] p-0 shadow-lg">
        <FilterEditor column={column} onDone={() => onOpenChange(false)} />
      </PopoverContent>
    </Popover>
  );
};

export const FilterBar: React.FC<Props> = ({ table, editing, onEditingChange, onReset }) => {
  const [adding, setAdding] = React.useState(false);
  const filterable = table.getAllLeafColumns().filter((c) => c.columnDef.meta?.filter);
  const active = filterable.filter((c) => describeFilter(c) !== null);
  const chips = filterable.filter((c) => describeFilter(c) !== null || editing === c.id);

  const hasState = active.length > 0 || !!table.getState().globalFilter || table.getState().grouping.length > 0 || table.getState().sorting.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-4 py-2">
      {chips.map((c) => (
        <Chip key={c.id} column={c} open={editing === c.id} onOpenChange={(o) => onEditingChange(o ? c.id : null)} />
      ))}

      <Popover open={adding} onOpenChange={setAdding}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-[12.5px] font-medium text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-text)]">
            <Plus className="h-3.5 w-3.5" /> Add filter
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[240px] rounded-xl border-[color:var(--oa-line-2)] p-0 shadow-lg">
          <Command>
            <CommandInput autoFocus placeholder="Filter by…" className="text-[13px]" />
            <CommandList>
              <CommandEmpty className="py-6 text-center text-[13px] text-[color:var(--oa-muted)]">No such field.</CommandEmpty>
              <CommandGroup>
                {filterable.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={c.columnDef.meta?.label ?? c.id}
                    onSelect={() => {
                      setAdding(false);
                      // let the add-popover close before the chip's opens
                      setTimeout(() => onEditingChange(c.id), 0);
                    }}
                    className="gap-2 text-[13px]"
                  >
                    <KindIcon kind={c.columnDef.meta?.filter} />
                    <span className="flex-1">{c.columnDef.meta?.label}</span>
                    {describeFilter(c) !== null ? <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--oa-accent)]" /> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {hasState ? (
        <button type="button" onClick={onReset} className="ml-auto inline-flex h-8 items-center gap-1 rounded-md px-2 text-[12.5px] font-medium text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-text)]">
          <X className="h-3.5 w-3.5" /> Reset view
        </button>
      ) : null}
    </div>
  );
};
